import classNames from 'classnames';
import { useNavigate } from 'react-router';
import { SlideInPanel } from '~/components/SlideInPanel';
import { paths } from '~/routing';
import { buildContractUrl } from '~/routing/urls';
import { CalendarEvent } from '../types';
import { EVENT_TYPE_LABELS, formatDisplayDate, getStatusLevel } from '../utils';
import { BADGE_BASE, OUTLINE_BUTTON, PRIMARY_BUTTON } from './controlStyles';

interface EventDetailPanelProps {
  event: CalendarEvent | null;
  onClose: () => void;
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) =>
  value == null || value === '' ? null : (
    <div className="flex justify-between gap-4 border-b border-grey-900 py-2">
      <span className="body-text text-grey-500">{label}</span>
      <span className="body-text-sb text-right text-grey-100">{value}</span>
    </div>
  );

export const EventDetailPanel = ({ event, onClose }: EventDetailPanelProps) => {
  const navigate = useNavigate();

  // Listing links still open their list pages. Contracts carry the exact ID;
  // billing events intentionally have no source link.
  const sourceLink = (() => {
    if (!event) return null;
    switch (event.sourceObjectType) {
      case 'contract':
        return { label: 'View contract', path: buildContractUrl(event.sourceObjectId) };
      case 'jobListing':
        return { label: 'View listing', path: paths.hlx.listings };
      case 'msaasListing':
        return { label: 'View B2B listing', path: paths.hlx.msaasListings };
      default:
        return null;
    }
  })();

  return (
    <SlideInPanel open={!!event} onClose={onClose} title={event?.title}>
      {event && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-1.5">
            <span
              className={classNames(
                BADGE_BASE,
                'bg-primary-t-900 text-primary-50 ring-primary-50/20'
              )}
            >
              {EVENT_TYPE_LABELS[event.eventType]}
            </span>
            <span className={classNames(BADGE_BASE, 'bg-grey-900 text-grey-300 ring-grey-700/20')}>
              {event.status}
            </span>
            {event.renewable && (
              <span
                className={classNames(
                  BADGE_BASE,
                  'bg-purple-100 text-purple-800 ring-purple-800/20'
                )}
              >
                Renewable
              </span>
            )}
            {getStatusLevel(event, new Date()) === 'warning' && (
              <span
                className={classNames(BADGE_BASE, 'bg-warning-50 text-warning-950 ring-warning/20')}
              >
                Credential readiness incomplete
              </span>
            )}
          </div>

          <div>
            <Row label="Date" value={formatDisplayDate(event.eventDate)} />
            <Row
              label="Date range"
              value={
                event.contractStartDate
                  ? `${formatDisplayDate(event.contractStartDate)} – ${
                      event.ongoing
                        ? 'Ongoing'
                        : event.contractEndDate
                          ? formatDisplayDate(event.contractEndDate)
                          : '—'
                    }`
                  : null
              }
            />
            <Row label="Facility" value={event.facilityName} />
            <Row label="Job type" value={event.jobType} />
            <Row
              label={event.identityState === 'masked' ? 'Counterparty (masked)' : 'Counterparty'}
              value={event.counterpartyDisplay}
            />
            <Row
              label="Your role"
              value={
                event.employerRole === 'BUYER'
                  ? 'Buying staffing capacity'
                  : event.employerRole === 'SELLER'
                    ? 'Providing staffing capacity'
                    : null
              }
            />
            <Row label="Contract #" value={event.contractNumber} />
            <Row
              label="Credential readiness"
              value={
                event.credentialReadinessState === 'unknown'
                  ? 'Unknown'
                  : event.credentialReadinessState
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            {sourceLink && (
              <button
                type="button"
                onClick={() => navigate(sourceLink.path)}
                className={classNames(PRIMARY_BUTTON, 'w-full')}
              >
                {sourceLink.label}
              </button>
            )}
            {event.contractListingId != null && (
              <button
                type="button"
                onClick={() => navigate(paths.hlx.msaasListings)}
                className={classNames(OUTLINE_BUTTON, 'w-full')}
              >
                View related contract listing
              </button>
            )}
            {event.jobListingId != null && event.sourceObjectType === 'contract' && (
              <button
                type="button"
                onClick={() => navigate(paths.hlx.listings)}
                className={classNames(OUTLINE_BUTTON, 'w-full')}
              >
                View source listing
              </button>
            )}
          </div>
        </div>
      )}
    </SlideInPanel>
  );
};
